import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
} from '@datachain/ui-sdk/common';
import { OrderedMap, Set } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import dayjs from '../core/configs/dayjs';
import { DcCoreElementType, DcCoreModule } from '../core/dc-core-types';
import { PermissionList } from '../core/domain/permission-list.entity';
import { DcDxGrids } from '../core/dx-types';
import { DxListsStorage } from '../core/services/dx-lists.storage';
import { ProjectStorage } from '../core/services/project.storage';
import { DcRouteUrlBuilder } from '../core/utils/dc-route-url-builder';
import {
	IProjectElement,
	IProjectElements,
} from '../requesters/core/models/project-elements.model';
import { IProjectMemberModel } from '../requesters/core/models/project-member.model';
import {
	IProjectGdStatResponse,
	IProjectHdStatResponse,
} from '../requesters/core/models/project-stat.model';
import { IProjectTraceResponse } from '../requesters/core/models/project-trace.model';
import { IProjectModel } from '../requesters/core/models/project.model';
import { ProjectElementsEntity } from './components/project-delete-popup/project-elements.entity';
import { ProjectTraceItemEntity } from './components/project-home/project-trace-item.entity';
import {
	IProjectTraceListExtra,
	ProjectTraceListEntity,
} from './components/project-home/project-trace-list.entity';
import { ProjectItemEntity } from './components/project-list/project-item.entity';
import {
	IProjectListExtra,
	ProjectListEntity,
} from './components/project-list/project-list.entity';
import { ProjectMemberEntity } from './components/project-list/project-member.entity';

@Injectable()
export class ProjectAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly dxListsStorage: DxListsStorage,
		private readonly projectStorage: ProjectStorage,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath
	) {}
	public generateProjectList(
		response: ReadonlyArray<IProjectModel>
	): ProjectListEntity {
		const list = this.generateProjectListEntityStructure();
		const items = response
			.reduce<Array<ProjectItemEntity>>((acc, item) => {
				const temp = this.generateProjectItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => a.compareTo(b));

		const orderedMap = OrderedMap<string, ProjectItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}

	public generateMemberList(
		response: ReadonlyArray<IProjectMemberModel>
	): Array<ProjectMemberEntity> {
		return response.map((p) =>
			ProjectMemberEntity.build({
				id: p.member_id,
				name: p.label,
				isAdmin: p.admin,
			})
		);
	}

	public generateProjectElementsEntity(
		response: IProjectElements
	): ProjectElementsEntity {
		return ProjectElementsEntity.build({
			connectors: this.generateElementDetailsArray(response.connectors),
			publications: this.generateElementDetailsArray(response.publications),
			expositions: this.generateElementDetailsArray(response.expositions),
			dashboards: this.generateElementDetailsArray(response.dashboards),
			medias: this.generateElementDetailsArray(response.medias),
			maps: this.generateElementDetailsArray(response.maps),
			grids: this.generateElementDetailsArray(response.grids),
			rankings: this.generateElementDetailsArray(response.classifications),
			charts: this.generateElementDetailsArray(response.charts),
			timelines: this.generateElementDetailsArray(response.time_lines),
			datablocks: this.generateElementDetailsArray(response.data_blocks),
			entities: this.generateElementDetailsArray(response.entities),
			depots: this.generateElementDetailsArray(response.depots),
			formulas: this.generateElementDetailsArray(
				response.formula_catalog_items
			),
			exportTemplates: this.generateElementDetailsArray(
				response.export_templates
			),
			exposedDatablocks: this.generateElementDetailsArray(
				response.dataBlock_expositions
			),
		});
	}

	public generateProjectElementStats(
		stat: IProjectGdStatResponse & IProjectHdStatResponse,
		module: DcCoreModule
	): Map<DcCoreElementType, number> {
		const result = Array<[DcCoreElementType, number]>();
		if (module === 'generics') {
			result.push([DcCoreElementType.CONNECTOR, stat.connector ?? 0]);
			result.push([DcCoreElementType.DEPOT, stat.repository ?? 0]);
			result.push([DcCoreElementType.ENTITY, stat.entity ?? 0]);
			result.push([DcCoreElementType.DATABLOCK, stat.datablock ?? 0]);
			result.push([DcCoreElementType.EXPOSITION, stat.exposition ?? 0]);
		}
		if (module === 'handle') {
			result.push([DcCoreElementType.DASHBOARD, stat.dashboard ?? 0]);
			result.push([DcCoreElementType.EXPOSITION, stat.exposition ?? 0]);
			result.push([DcCoreElementType.MAP, stat.map ?? 0]);
			result.push([DcCoreElementType.TIMELINE, stat.timeline ?? 0]);
			result.push([DcCoreElementType.CHART, stat.chart ?? 0]);
			result.push([DcCoreElementType.GRID, stat.grid ?? 0]);
			result.push([DcCoreElementType.MEDIA, stat.media ?? 0]);
		}
		return new Map(result);
	}

	public generateTraceList(
		response: ReadonlyArray<IProjectTraceResponse>,
		element: DcCoreElementType
	): ProjectTraceListEntity {
		const projectId = this.projectStorage.getProjectId();
		const list = this.generateProjectTraceListStructure();
		const items = response.reduce<Array<ProjectTraceItemEntity>>(
			(acc, item, idx) => {
				const temp = ProjectTraceItemEntity.build({
					id: idx,
					action: item.action?.toString(),
					elementLabel: item.element_lib,
					modifiedBy: item.user_id,
					modificationDate: dayjs(item.date),
					elementUrl: DcRouteUrlBuilder.buildEditPageUrl(
						projectId,
						item.element_id,
						element,
						item.subtype
					),
				});
				acc.push(temp);
				return acc;
			},
			[]
		);
		const orderedMap = OrderedMap<string, ProjectTraceItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}

	public generatePermissions(response: ReadonlyArray<string>): PermissionList {
		return PermissionList.build({
			list: Set(response),
		});
	}

	private generateElementDetailsArray(
		response: Array<IProjectElement> | undefined
	): Array<{
		id: number;
		label: string;
		link: string;
	}> {
		return response
			? response.map((c) => ({
					id: c.id ?? -1,
					label: c.label ?? '',
					link: '',
			  }))
			: [];
	}

	private generateProjectItemEntity(project: IProjectModel): ProjectItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return ProjectItemEntity.build({
			id: project.id,
			label: project.metadata?.label ?? '',
			code: project.metadata?.code ?? '',
			description: project.metadata?.description ?? '',
			isFavorite: project.favorite_project,
			isDefault: project.default_project,
			isExpired: project.expired,
			isActive: project.metadata?.actif,
			isAdmin: project.admin,
			expirationDate: dayjs(project.expiration_date),
			accessDate: dayjs(project.last_access?.date),
			accessedBy: project.last_access?.user
				? `${project.last_access?.user.first_name} ${project.last_access?.user.last_name}`
				: '',
			memberCount: project.member_count,
			adminCount: project.administrators?.length ?? 0,
			contextCount: project.context_count,
			version: project.version,
			imageUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				project.metadata?.icon_id ?? -1
			}`,
			tags: this.tagAdapter.generateResponseMetadataTag(
				project.metadata?.tags ?? []
			),
			updateDate: dayjs(project.metadata?.update_date),
			creationDate: dayjs(project.metadata?.creation_date),
			updatedBy: project.metadata?.updated_by,
			createdBy: project.metadata?.created_by,
		});
	}

	private generateProjectListEntityStructure(): ProjectListEntity {
		let availableCols: Array<IProjectListExtra> = [
			{
				field: 'label',
				alias: $localize`:i18n=@@project.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 0,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@project.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 1,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@project.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'accessDate',
				alias: $localize`:i18n=@@project.list.column.accessDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 3,
			},
			{
				field: 'accessedByAvatar',
				alias: $localize`:i18n=@@project.list.column.accessedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
		];
		availableCols = this.dxListsStorage.synchronize<
			IProjectListExtra,
			ProjectItemEntity
		>(DcDxGrids.ProjectListStandalone, availableCols);
		return ProjectListEntity.build(availableCols);
	}

	private generateProjectTraceListStructure(): ProjectTraceListEntity {
		let availableCols: Array<IProjectTraceListExtra> = [
			{
				field: 'action',
				alias: $localize`:i18n=@@project.trace.column.action:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'elementLabel',
				alias: $localize`:i18n=@@project.trace.column.element-label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 1,
			},
			{
				field: 'modificationDate',
				alias: $localize`:i18n=@@project.trace.column.modificationDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'modifiedBy',
				alias: $localize`:i18n=@@project.trace.column.modifiedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: false,
				visibleIndex: 3,
			},
		];
		availableCols = this.dxListsStorage.synchronize<
			IProjectTraceListExtra,
			ProjectTraceItemEntity
		>(DcDxGrids.ProjectTraceListStandalone, availableCols);
		return ProjectTraceListEntity.build(availableCols);
	}
}
